import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Plus,
  Eye,
  Edit2,
  Trash2,
  AlertCircle,
  BarChart3,
  Filter,
  Search,
  ChevronUp,
  ChevronDown,
  Loader,
  ClipboardList,
} from 'lucide-react';
import FindingForm from './FindingForm';
import CorrectiveActionsPanel from './CorrectiveActionsPanel';
import ExportButton from './ExportButton';
import * as auditFindingsService from '../../services/auditFindingsService';
import './index.css';

const SEVERITY_OPTIONS = ['CRITICAL', 'MAJOR', 'MINOR'];
const STATUS_OPTIONS = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
const STANDARDS = [
  'ISO 27001',
  'ISO 9001',
  'ISO 45001',
  'ISCC',
];

const ITEMS_PER_PAGE = 10;

/**
 * AuditFindings Main Component
 */
const AuditFindings = () => {
  const { projectId } = useParams();

  // State: Data
  const [findings, setFindings] = useState([]);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [statistics, setStatistics] = useState({});

  // State: UI
  const [activeTab, setActiveTab] = useState('list');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedStandard, setSelectedStandard] = useState('');
  const [sortBy, setSortBy] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // State: Modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Load findings on mount and when projectId changes
  useEffect(() => {
    if (projectId) {
      loadFindings();
      loadStatistics();
    }
  }, [projectId]);

  // Load findings
  const loadFindings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filters = {
        severity: selectedSeverity,
        status: selectedStatus,
        standard: selectedStandard,
        searchText: searchText,
      };
      const data = await auditFindingsService.getFindings(projectId, filters);
      setFindings(Array.isArray(data) ? data : []);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error loading findings:', err);
      setError('Failed to load findings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, selectedSeverity, selectedStatus, selectedStandard, searchText]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    try {
      const data = await auditFindingsService.getFindingsStatistics(projectId);
      setStatistics(data || {});
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  }, [projectId]);

  // Sort findings
  const sortedFindings = useMemo(() => {
    const sorted = [...findings].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return sorted;
  }, [findings, sortBy, sortOrder]);

  // Paginate findings
  const paginatedFindings = useMemo(() => {
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIdx = startIdx + ITEMS_PER_PAGE;
    return sortedFindings.slice(startIdx, endIdx);
  }, [sortedFindings, currentPage]);

  const totalPages = Math.ceil(sortedFindings.length / ITEMS_PER_PAGE);

  // Handle filter change
  const handleFilterChange = useCallback((filterType, value) => {
    switch (filterType) {
      case 'search':
        setSearchText(value);
        break;
      case 'severity':
        setSelectedSeverity(value);
        break;
      case 'status':
        setSelectedStatus(value);
        break;
      case 'standard':
        setSelectedStandard(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
  }, []);

  // Handle sort change
  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }, [sortBy, sortOrder]);

  // Handle finding submission (create or update)
  const handleSubmitFinding = useCallback(async (formData) => {
    setIsLoading(true);
    setError(null);
    try {
      if (selectedFinding) {
        await auditFindingsService.updateFinding(selectedFinding.id, formData);
        setSuccessMessage('Finding updated successfully!');
      } else {
        await auditFindingsService.createFinding(projectId, formData);
        setSuccessMessage('Finding created successfully!');
      }
      setSelectedFinding(null);
      setActiveTab('list');
      loadFindings();
      loadStatistics();
    } catch (err) {
      console.error('Error submitting finding:', err);
      setError('Failed to save finding. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, selectedFinding, loadFindings, loadStatistics]);

  // Handle finding view
  const handleViewFinding = useCallback((finding) => {
    setSelectedFinding(finding);
    setShowDetailModal(true);
  }, []);

  // Handle finding edit
  const handleEditFinding = useCallback((finding) => {
    setSelectedFinding(finding);
    setActiveTab('form');
  }, []);

  // Handle finding delete
  const handleDeleteFinding = useCallback(async () => {
    if (!showDeleteConfirm) return;

    setIsLoading(true);
    setError(null);
    try {
      await auditFindingsService.deleteFinding(showDeleteConfirm.id);
      setSuccessMessage('Finding deleted successfully!');
      setShowDeleteConfirm(null);
      loadFindings();
      loadStatistics();
    } catch (err) {
      console.error('Error deleting finding:', err);
      setError('Failed to delete finding. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [showDeleteConfirm, loadFindings, loadStatistics]);

  // Handle creating new finding
  const handleNewFinding = useCallback(() => {
    setSelectedFinding(null);
    setActiveTab('form');
  }, []);

  // Clear messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const getSeverityBadge = (severity) => {
    const colors = {
      CRITICAL: '#dc3545',
      MAJOR: '#fd7e14',
      MINOR: '#ffc107',
    };
    return colors[severity] || '#6c757d';
  };

  const getSeverityBadgeClass = (severity) => {
    const classes = {
      CRITICAL: 'badge bg-danger',
      MAJOR: 'badge bg-warning text-dark',
      MINOR: 'badge bg-info text-dark',
    };
    return classes[severity] || 'badge bg-secondary';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      OPEN: 'badge bg-danger',
      IN_PROGRESS: 'badge bg-warning text-dark',
      RESOLVED: 'badge bg-success',
      CLOSED: 'badge bg-secondary',
    };
    return classes[status] || 'badge bg-secondary';
  };

  const getStatusBadge = (status) => {
    const classes = {
      OPEN: 'status-badge open',
      IN_PROGRESS: 'status-badge in-progress',
      RESOLVED: 'status-badge resolved',
      CLOSED: 'status-badge closed',
    };
    return classes[status] || '';
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <ChevronDown size={14} className="text-muted" />;
    return sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  if (!projectId) {
    return (
      <div className="audit-findings-container">
        <div className="alert alert-danger d-flex align-items-center gap-2">
          <AlertCircle size={20} className="flex-shrink-0" />
          <span>Project ID not found. Please navigate from the projects page.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-findings-container">
      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show findings-alert" role="alert">
          <strong>Success!</strong> {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage(null)}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show findings-alert" role="alert">
          <strong>Error:</strong> {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} />
        </div>
      )}

      {/* Page Header Card */}
      <div className="findings-header-card card mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h4 className="findings-page-title mb-1">
                <ClipboardList size={22} className="me-2 text-primary" />
                Audit Findings
              </h4>
              <p className="text-muted mb-0 small">
                Project <span className="fw-semibold text-primary">#{projectId}</span> &mdash; Manage and track audit findings
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleNewFinding}
                disabled={isLoading}
              >
                <Plus size={16} />
                New Finding
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="findings-tabs card">
        <div className="card-header p-0">
          <ul className="nav nav-tabs card-header-tabs px-3 pt-1" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'list' ? 'active' : ''}`}
                onClick={() => setActiveTab('list')}
                role="tab"
              >
                Findings List
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'form' ? 'active' : ''}`}
                onClick={() => setActiveTab('form')}
                role="tab"
              >
                {selectedFinding ? 'Edit Finding' : 'Create Finding'}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'statistics' ? 'active' : ''}`}
                onClick={() => setActiveTab('statistics')}
                role="tab"
              >
                <BarChart3 size={15} className="me-1" />
                Statistics
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body tab-content-body">
          {/* TAB 1: Findings List */}
          {activeTab === 'list' && (
            <div>
              {/* Filters Panel */}
              <div className="card findings-filter-card mb-3">
                <div className="card-body py-2">
                  <div className="row g-2 align-items-center">
                    <div className="col-12 col-md-4">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white">
                          <Search size={15} className="text-muted" />
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search by requirement..."
                          value={searchText}
                          onChange={(e) => handleFilterChange('search', e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div className="col-6 col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={selectedSeverity}
                        onChange={(e) => handleFilterChange('severity', e.target.value)}
                        disabled={isLoading}
                      >
                        <option value="">All Severities</option>
                        {SEVERITY_OPTIONS.map((sev) => (
                          <option key={sev} value={sev}>
                            {sev}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-6 col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={selectedStatus}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        disabled={isLoading}
                      >
                        <option value="">All Statuses</option>
                        {STATUS_OPTIONS.map((stat) => (
                          <option key={stat} value={stat}>
                            {stat}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-6 col-md-2">
                      <select
                        className="form-select form-select-sm"
                        value={selectedStandard}
                        onChange={(e) => handleFilterChange('standard', e.target.value)}
                        disabled={isLoading}
                      >
                        <option value="">All Standards</option>
                        {STANDARDS.map((std) => (
                          <option key={std} value={std}>
                            {std}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-6 col-md-2 d-flex justify-content-end">
                      <ExportButton
                        findings={findings}
                        project={{ id: projectId, name: 'Project' }}
                        auditor="Auditor"
                        isLoading={isLoading}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {isLoading ? (
                <div className="loading-spinner">
                  <div className="d-flex flex-column align-items-center gap-2 text-muted">
                    <Loader size={32} className="spinner-icon text-primary" />
                    <small>Loading findings...</small>
                  </div>
                </div>
              ) : paginatedFindings.length > 0 ? (
                <>
                  {/* Findings Table */}
                  <div className="table-responsive findings-list">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th
                            onClick={() => handleSort('standard')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >
                            Standard {getSortIcon('standard')}
                          </th>
                          <th
                            onClick={() => handleSort('requirement')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >
                            Requirement {getSortIcon('requirement')}
                          </th>
                          <th
                            onClick={() => handleSort('severity')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >
                            Severity {getSortIcon('severity')}
                          </th>
                          <th
                            onClick={() => handleSort('status')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >
                            Status {getSortIcon('status')}
                          </th>
                          <th
                            onClick={() => handleSort('createdDate')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >
                            Date {getSortIcon('createdDate')}
                          </th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedFindings.map((finding) => (
                          <tr key={finding.id} className="finding-row">
                            <td>
                              <span className="fw-semibold text-primary">{finding.standard}</span>
                            </td>
                            <td>
                              <span className="finding-requirement" title={finding.requirement}>
                                {finding.requirement.length > 50
                                  ? finding.requirement.substring(0, 50) + '...'
                                  : finding.requirement}
                              </span>
                            </td>
                            <td>
                              <span className={getSeverityBadgeClass(finding.severity)}>
                                {finding.severity}
                              </span>
                            </td>
                            <td>
                              <span className={getStatusBadgeClass(finding.status)}>
                                {finding.status === 'IN_PROGRESS' ? 'In Progress' : finding.status.charAt(0) + finding.status.slice(1).toLowerCase()}
                              </span>
                            </td>
                            <td className="text-muted small">
                              {finding.createdDate
                                ? new Date(finding.createdDate).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td>
                              <div className="d-flex justify-content-center gap-1">
                                <button
                                  className="btn btn-sm btn-outline-info findings-action-btn"
                                  onClick={() => handleViewFinding(finding)}
                                  title="View details"
                                  disabled={isLoading}
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-primary findings-action-btn"
                                  onClick={() => handleEditFinding(finding)}
                                  title="Edit finding"
                                  disabled={isLoading}
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger findings-action-btn"
                                  onClick={() => setShowDeleteConfirm(finding)}
                                  title="Delete finding"
                                  disabled={isLoading}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination-container">
                      <span className="pagination-info">
                        Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                        {Math.min(currentPage * ITEMS_PER_PAGE, sortedFindings.length)} of{' '}
                        {sortedFindings.length} findings
                      </span>
                      <nav aria-label="Findings pagination">
                        <ul className="pagination pagination-sm mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                            (page) => (
                              <li
                                key={page}
                                className={`page-item ${currentPage === page ? 'active' : ''}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            )
                          )}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <ClipboardList size={52} strokeWidth={1.2} />
                  </div>
                  <h6 className="mt-3 mb-1">No findings found</h6>
                  <p className="text-muted small mb-3">
                    {searchText || selectedSeverity || selectedStatus || selectedStandard
                      ? 'Try adjusting your filters to see more results.'
                      : 'Get started by creating your first audit finding.'}
                  </p>
                  {!searchText && !selectedSeverity && !selectedStatus && !selectedStandard && (
                    <button className="btn btn-primary btn-sm" onClick={handleNewFinding}>
                      <Plus size={14} className="me-1" />
                      New Finding
                    </button>
                  )}
                </div>
              )}

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm && (
                <div
                  className="modal d-block"
                  style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                >
                  <div className="modal-dialog modal-dialog-centered modal-sm">
                    <div className="modal-content">
                      <div className="modal-header bg-danger text-white">
                        <h5 className="modal-title">
                          <Trash2 size={16} className="me-2" />
                          Delete Finding?
                        </h5>
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          onClick={() => setShowDeleteConfirm(null)}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="modal-body">
                        <p className="mb-0 text-muted">
                          This action <strong className="text-danger">cannot be undone</strong>. Are you sure you want to delete this finding?
                        </p>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowDeleteConfirm(null)}
                          disabled={isLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={handleDeleteFinding}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <Loader size={13} className="spinner-icon me-1" />
                              Deleting...
                            </>
                          ) : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Create/Edit Form */}
          {activeTab === 'form' && (
            <div>
              <FindingForm
                finding={selectedFinding}
                onSubmit={handleSubmitFinding}
                onCancel={() => {
                  setSelectedFinding(null);
                  setActiveTab('list');
                }}
                isLoading={isLoading}
              />
            </div>
          )}

          {/* TAB 3: Statistics Dashboard */}
          {activeTab === 'statistics' && (
            <div>
              {/* Summary Cards */}
              <div className="row g-3 mb-4">
                <div className="col-6 col-lg-3">
                  <div className="stat-card stat-card-primary">
                    <div className="stat-card-body">
                      <div className="stat-label">Total Findings</div>
                      <div className="stat-value">{statistics.total || 0}</div>
                    </div>
                    <div className="stat-icon">
                      <ClipboardList size={32} />
                    </div>
                  </div>
                </div>

                <div className="col-6 col-lg-3">
                  <div className="stat-card stat-card-danger">
                    <div className="stat-card-body">
                      <div className="stat-label">Critical</div>
                      <div className="stat-value">{statistics.critical || 0}</div>
                    </div>
                    <div className="stat-icon">
                      <AlertCircle size={32} />
                    </div>
                  </div>
                </div>

                <div className="col-6 col-lg-3">
                  <div className="stat-card stat-card-warning">
                    <div className="stat-card-body">
                      <div className="stat-label">Major</div>
                      <div className="stat-value">{statistics.major || 0}</div>
                    </div>
                    <div className="stat-icon">
                      <Filter size={32} />
                    </div>
                  </div>
                </div>

                <div className="col-6 col-lg-3">
                  <div className="stat-card stat-card-success">
                    <div className="stat-card-body">
                      <div className="stat-label">Resolved / Closed</div>
                      <div className="stat-value">
                        {(statistics.resolved || 0) + (statistics.closed || 0)}
                      </div>
                    </div>
                    <div className="stat-icon">
                      <BarChart3 size={32} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Distribution Charts */}
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="card h-100">
                    <div className="card-header">
                      <h6 className="mb-0 fw-semibold">Status Distribution</h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-danger">OPEN</span>
                            <span className="small text-muted">Open</span>
                          </div>
                          <span className="fw-semibold">{statistics.open || 0}</span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar bg-danger"
                            style={{
                              width: `${
                                statistics.total
                                  ? ((statistics.open || 0) / statistics.total) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-warning text-dark">IN PROGRESS</span>
                            <span className="small text-muted">In Progress</span>
                          </div>
                          <span className="fw-semibold">{statistics.inProgress || 0}</span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar bg-warning"
                            style={{
                              width: `${
                                statistics.total
                                  ? ((statistics.inProgress || 0) / statistics.total) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-success">RESOLVED</span>
                            <span className="small text-muted">Resolved</span>
                          </div>
                          <span className="fw-semibold">{statistics.resolved || 0}</span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar bg-success"
                            style={{
                              width: `${
                                statistics.total
                                  ? ((statistics.resolved || 0) / statistics.total) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-secondary">CLOSED</span>
                            <span className="small text-muted">Closed</span>
                          </div>
                          <span className="fw-semibold">{statistics.closed || 0}</span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar bg-secondary"
                            style={{
                              width: `${
                                statistics.total
                                  ? ((statistics.closed || 0) / statistics.total) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Severity Distribution */}
                <div className="col-12 col-md-6">
                  <div className="card h-100">
                    <div className="card-header">
                      <h6 className="mb-0 fw-semibold">Severity Distribution</h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-danger">CRITICAL</span>
                            <span className="small text-muted">Critical</span>
                          </div>
                          <span className="fw-semibold">{statistics.critical || 0}</span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar bg-danger"
                            style={{
                              width: `${
                                statistics.total
                                  ? ((statistics.critical || 0) / statistics.total) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-warning text-dark">MAJOR</span>
                            <span className="small text-muted">Major</span>
                          </div>
                          <span className="fw-semibold">{statistics.major || 0}</span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar bg-warning"
                            style={{
                              width: `${
                                statistics.total
                                  ? ((statistics.major || 0) / statistics.total) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-info text-dark">MINOR</span>
                            <span className="small text-muted">Minor</span>
                          </div>
                          <span className="fw-semibold">{statistics.minor || 0}</span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar bg-info"
                            style={{
                              width: `${
                                statistics.total
                                  ? ((statistics.minor || 0) / statistics.total) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedFinding && (
        <div
          className="modal d-block finding-detail-modal"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header finding-detail-header">
                <div>
                  <h5 className="modal-title mb-0">Finding Details</h5>
                  <small className="text-muted">
                    {selectedFinding.standard} &mdash;{' '}
                    <span className={getSeverityBadgeClass(selectedFinding.severity)}>
                      {selectedFinding.severity}
                    </span>
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-12 col-sm-6">
                    <div className="finding-detail-section">
                      <h6>Standard</h6>
                      <p>{selectedFinding.standard || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-6 col-sm-3">
                    <div className="finding-detail-section">
                      <h6>Severity</h6>
                      <span className={getSeverityBadgeClass(selectedFinding.severity)}>
                        {selectedFinding.severity}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-sm-3">
                    <div className="finding-detail-section">
                      <h6>Status</h6>
                      <span className={getStatusBadgeClass(selectedFinding.status)}>
                        {selectedFinding.status === 'IN_PROGRESS' ? 'In Progress' : selectedFinding.status.charAt(0) + selectedFinding.status.slice(1).toLowerCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="finding-detail-section">
                  <h6>Requirement</h6>
                  <p>{selectedFinding.requirement || 'N/A'}</p>
                </div>

                <div className="finding-detail-section">
                  <h6>Description</h6>
                  <p>{selectedFinding.description || 'N/A'}</p>
                </div>

                {selectedFinding.evidence && (
                  <div className="finding-detail-section">
                    <h6>Evidence</h6>
                    <p>{selectedFinding.evidence}</p>
                  </div>
                )}

                {selectedFinding.rootCause && (
                  <div className="finding-detail-section">
                    <h6>Root Cause</h6>
                    <p>{selectedFinding.rootCause}</p>
                  </div>
                )}

                {/* Corrective Actions Panel */}
                {selectedFinding.id && (
                  <CorrectiveActionsPanel
                    findingId={selectedFinding.id}
                    correctiveActions={selectedFinding.correctiveActions || []}
                    onAdd={async (data) => {
                      await auditFindingsService.addCorrectiveAction(selectedFinding.id, data);
                      loadFindings();
                    }}
                    onUpdate={async (caId, data) => {
                      await auditFindingsService.updateCorrectiveAction(
                        selectedFinding.id,
                        caId,
                        data
                      );
                      loadFindings();
                    }}
                    onDelete={async (caId) => {
                      await auditFindingsService.deleteCorrectiveAction(
                        selectedFinding.id,
                        caId
                      );
                      loadFindings();
                    }}
                    isLoading={isLoading}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowDetailModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setShowDetailModal(false);
                    handleEditFinding(selectedFinding);
                  }}
                >
                  <Edit2 size={14} className="me-1" />
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditFindings;
